import { configureStore } from '@reduxjs/toolkit';
import { applicationAPI } from './services/client/profile';
import { consumerFeesWorksheetAPI } from './services/client/feesWorksheet';
import application from './slices/application';
import onboarding from './slices/onboarding';
import chatMessage from './slices/chatMessage';
import loanTools from './slices/loanTools';
import userRole from './slices/userRole';
import authReducer from './slices/authSlice';
import { onboardingAPI } from './services/client/onboarding';
import { documentUploadAPI } from './services/client/documentUpload';
import { pipelineAPI } from './services/client/pipeline';
import { bulkUploadAPI } from './services/client/bulkUpload';
import { documentAPI } from './services/client/document';
import { dashboardAPI } from './services/client/dashboard';
import { truvConsumerAPI } from './services/client/truv';
import { loanInformationAPI } from './services/admin/loanInformation';
import { borrowerProfileAPI } from './services/borrower-profile';
import { chatAPI } from './services/chatHistory';
import { loanProcessorAPI } from './services/loanProcessor';
import { adminFeesworksheetAPI } from './services/admin/feesworksheet';
import { adminPipelineAPI } from './services/admin/pipeline';
import { productAndPricingAPI } from './services/admin/product-pricing';
import { adminConsumerAPI } from './services/admin/adminConsumer';
import { adminEmployeeAPI } from './services/admin/adminEmployee';
import { adminDocumentAPI } from './services/admin/document';
import { adminToolsAPI } from './services/admin/adminTools';
import { quickPriceAPI } from './services/admin/quickPrice';
import { reportsAPI } from './services/admin/reports';
import { notificationsAPI } from './services/notifications';
import { qualityCheckAPI } from './services/admin/qualityCheck';
import { userManagementV2API } from './services/admin/userManagementV2';
import { branchManagementAPI } from './services/admin/branchManagement';
import { pricingIntegrationsApi } from './services/integrations/pricing';
import { consumerToolsAPI } from './services/client/tools';
import { compositeInitAPI } from './services/client/compositeInit';
import { underwritingAPI } from './services/admin/underwriting';
import { docProcessingAPI } from './services/client/docProcessing';
import { whatsNextAPI, whatsNextRefreshMiddleware } from './services/admin/whatsNext';
import { rbacAPI } from './services/admin/rbac';

// Invalidate the composite dashboard cache when related mutations complete.
// Only invalidate for mutations that actually change dashboard-visible data
// (pipeline status, documents). Profile section edits don't affect the lean
// dashboard payload, so 'profile' is excluded to avoid unnecessary refetches
// that cause DB contention when the user is editing on the profile page.
const INVALIDATION_API_NAMES = ['pipeline', 'Document', 'documentUploadAPI'];
const compositeInvalidationMiddleware = (storeAPI) => (next) => (action) => {
  const result = next(action);
  if (action.type?.endsWith('/fulfilled') && action.meta?.arg?.type === 'mutation') {
    const apiName = action.type.split('/')[0];
    if (INVALIDATION_API_NAMES.includes(apiName)) {
      storeAPI.dispatch(compositeInitAPI.util.invalidateTags(['dashboardInit']));
    }
  }
  return result;
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    application,
    onboard: onboarding,
    chatMessage,
    loanTools,
    userRole,
    [onboardingAPI.reducerPath]: onboardingAPI.reducer,
    [applicationAPI.reducerPath]: applicationAPI.reducer,
    [consumerFeesWorksheetAPI.reducerPath]: consumerFeesWorksheetAPI.reducer,
    [pipelineAPI.reducerPath]: pipelineAPI.reducer,
    [bulkUploadAPI.reducerPath]: bulkUploadAPI.reducer,
    [documentUploadAPI.reducerPath]: documentUploadAPI.reducer,
    [documentAPI.reducerPath]: documentAPI.reducer,
    [dashboardAPI.reducerPath]: dashboardAPI.reducer,
    [truvConsumerAPI.reducerPath]: truvConsumerAPI.reducer,
    [loanInformationAPI.reducerPath]: loanInformationAPI.reducer,
    [adminFeesworksheetAPI.reducerPath]: adminFeesworksheetAPI.reducer,
    [borrowerProfileAPI.reducerPath]: borrowerProfileAPI.reducer,
    [chatAPI.reducerPath]: chatAPI.reducer,
    [loanProcessorAPI.reducerPath]: loanProcessorAPI.reducer,
    [adminPipelineAPI.reducerPath]: adminPipelineAPI.reducer,
    [productAndPricingAPI.reducerPath]: productAndPricingAPI.reducer,
    [qualityCheckAPI.reducerPath]: qualityCheckAPI.reducer,
    [adminConsumerAPI.reducerPath]: adminConsumerAPI.reducer,
    [adminEmployeeAPI.reducerPath]: adminEmployeeAPI.reducer,
    [adminDocumentAPI.reducerPath]: adminDocumentAPI.reducer,
    [adminToolsAPI.reducerPath]: adminToolsAPI.reducer,
    [quickPriceAPI.reducerPath]: quickPriceAPI.reducer,
    [reportsAPI.reducerPath]: reportsAPI.reducer,
    [notificationsAPI.reducerPath]: notificationsAPI.reducer,
    [userManagementV2API.reducerPath]: userManagementV2API.reducer,
    [branchManagementAPI.reducerPath]: branchManagementAPI.reducer,
    [pricingIntegrationsApi.reducerPath]: pricingIntegrationsApi.reducer,
    [consumerToolsAPI.reducerPath]: consumerToolsAPI.reducer,
    [compositeInitAPI.reducerPath]: compositeInitAPI.reducer,
    [underwritingAPI.reducerPath]: underwritingAPI.reducer,
    [docProcessingAPI.reducerPath]: docProcessingAPI.reducer,
    [whatsNextAPI.reducerPath]: whatsNextAPI.reducer,
    [rbacAPI.reducerPath]: rbacAPI.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      onboardingAPI.middleware,
      applicationAPI.middleware,
      consumerFeesWorksheetAPI.middleware,
      pipelineAPI.middleware,
      bulkUploadAPI.middleware,
      documentUploadAPI.middleware,
      documentAPI.middleware,
      dashboardAPI.middleware,
      truvConsumerAPI.middleware,
      loanInformationAPI.middleware,
      borrowerProfileAPI.middleware,
      chatAPI.middleware,
      loanProcessorAPI.middleware,
      adminFeesworksheetAPI.middleware,
      adminPipelineAPI.middleware,
      qualityCheckAPI.middleware,
      productAndPricingAPI.middleware,
      adminConsumerAPI.middleware,
      adminEmployeeAPI.middleware,
      adminDocumentAPI.middleware,
      reportsAPI.middleware,
      adminToolsAPI.middleware,
      quickPriceAPI.middleware,
      notificationsAPI.middleware,
      userManagementV2API.middleware,
      branchManagementAPI.middleware,
      pricingIntegrationsApi.middleware,
      consumerToolsAPI.middleware,
      compositeInitAPI.middleware,
      underwritingAPI.middleware,
      docProcessingAPI.middleware,
      whatsNextAPI.middleware,
      rbacAPI.middleware,
      whatsNextRefreshMiddleware,
      compositeInvalidationMiddleware
    ),
});
